import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, PenTool, Eraser, Check, Info } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignaturePage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [advocateName, setAdvocateName] = useState("");
  const [advocateDetails, setAdvocateDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signatureMode, setSignatureMode] = useState("draw"); // "draw", "upload", "retain"
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [existingSignature, setExistingSignature] = useState(null);
  const [docStatus, setDocStatus] = useState(null);

  useEffect(() => {
    const fetchDocumentStatus = async () => {
      try {
        const response = await axios.get(`${API}/document-status/${documentId}`);
        setDocStatus(response.data);
      } catch (error) {
        console.error('Failed to load document status:', error);
      }
    };

    if (documentId) {
      fetchDocumentStatus();
    }
  }, [documentId]);

  const handleReturnToExtract = () => {
    const extractionId = docStatus?.original_extraction_id || docStatus?.extraction_id;
    if (extractionId) {
      navigate(`/extract/${extractionId}`, {
        state: { resetFormatSelectionOnReturn: true }
      });
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Set white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Prevent scrolling when touching the canvas
      const preventScroll = (e) => {
        e.preventDefault();
      };
      
      canvas.addEventListener('touchstart', preventScroll, { passive: false });
      canvas.addEventListener('touchmove', preventScroll, { passive: false });
      
      return () => {
        canvas.removeEventListener('touchstart', preventScroll);
        canvas.removeEventListener('touchmove', preventScroll);
      };
    }
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
    
    // Handle mouse events
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedSignature(event.target.result);
        setSignatureMode("upload");
        toast.success("Signature image uploaded");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSkip = async () => {
    // Skip signature and go directly to download
    navigate(`/download/${documentId}`);
  };

  const handleRetain = () => {
    setSignatureMode("retain");
    toast.info("Existing signature will be retained");
  };

  const handleSubmit = async () => {
    if (!advocateName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setSubmitting(true);

    try {
      let signatureImage = null;

      // Handle different signature modes
      if (signatureMode === "draw") {
        // Check if signature is drawn
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Check if canvas has any non-white pixels
        let hasSignature = false;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
            hasSignature = true;
            break;
          }
        }

        if (!hasSignature) {
          toast.error("Please draw your signature in the canvas or choose another option");
          setSubmitting(false);
          return;
        }

        // Convert canvas to base64
        signatureImage = canvas.toDataURL('image/png');
      } else if (signatureMode === "upload") {
        if (!uploadedSignature) {
          toast.error("Please upload a signature image");
          setSubmitting(false);
          return;
        }
        signatureImage = uploadedSignature;
      } else if (signatureMode === "retain") {
        // Use existing signature - send null to indicate retention
        signatureImage = null;
      }

      // Submit signature
      await axios.post(`${API}/add-signature`, {
        document_id: documentId,
        signature_image: signatureImage,
        advocate_name: advocateName,
        advocate_details: advocateDetails,
        retain_existing: signatureMode === "retain"
      });

      toast.success(signatureMode === "retain" ? "Existing signature retained!" : "Signature added successfully!");
      navigate(`/download/${documentId}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add signature. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                data-testid="back-button"
                variant="ghost"
                onClick={handleReturnToExtract}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Add Your Signature</h1>
                <p className="text-sm text-gray-600">Complete the legal opinion with your digital signature</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Upload</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Review</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">3</div>
              <span className="text-sm font-medium text-blue-600">Signature</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm font-semibold">4</div>
              <span className="text-sm font-medium text-gray-400">Download</span>
            </div>
          </div>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Your Digital Signature</p>
                  <p>This signature will be embedded in both PDF and DOCX versions of your legal opinion document.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advocate Details */}
          <Card data-testid="advocate-details-card">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle>Advocate Information</CardTitle>
              <CardDescription>Enter your professional details</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="advocateName" className="text-sm font-semibold">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="advocateName"
                  data-testid="advocate-name-input"
                  value={advocateName}
                  onChange={(e) => setAdvocateName(e.target.value)}
                  placeholder="Enter your complete name as it appears on Bar Council records"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="advocateDetails" className="text-sm font-semibold">Address & Contact Details (Optional)</Label>
                <Textarea
                  id="advocateDetails"
                  data-testid="advocate-details-input"
                  value={advocateDetails}
                  onChange={(e) => setAdvocateDetails(e.target.value)}
                  placeholder="Complete address, Bar Council enrollment number, phone, email, etc."
                  className="mt-1"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Signature Options */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle>Signature Options</CardTitle>
              <CardDescription>Choose how you want to add your signature</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Button
                  variant={signatureMode === "draw" ? "default" : "outline"}
                  onClick={() => setSignatureMode("draw")}
                  className="h-20 flex flex-col items-center justify-center"
                >
                  <PenTool className="w-6 h-6 mb-1" />
                  <span>Draw</span>
                </Button>
                <Button
                  variant={signatureMode === "upload" ? "default" : "outline"}
                  onClick={() => fileInputRef.current?.click()}
                  className="h-20 flex flex-col items-center justify-center"
                >
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload</span>
                </Button>
                <Button
                  variant={signatureMode === "retain" ? "default" : "outline"}
                  onClick={handleRetain}
                  className="h-20 flex flex-col items-center justify-center"
                >
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Retain Existing</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="h-20 flex flex-col items-center justify-center border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Skip</span>
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />

              {/* Draw Mode */}
              {signatureMode === "draw" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium">Draw your signature below:</p>
                    <Button
                      data-testid="clear-signature-button"
                      variant="outline"
                      size="sm"
                      onClick={clearCanvas}
                    >
                      <Eraser className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  </div>
                  <div className="border-2 border-gray-300 rounded-lg bg-white shadow-inner touch-none">
                    <canvas
                      ref={canvasRef}
                      data-testid="signature-canvas"
                      width={700}
                      height={200}
                      className="cursor-crosshair w-full touch-none"
                      style={{ touchAction: 'none' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      onTouchCancel={stopDrawing}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">Use mouse or touch to draw</p>
                </div>
              )}

              {/* Upload Mode */}
              {signatureMode === "upload" && uploadedSignature && (
                <div>
                  <p className="text-sm font-medium mb-3">Uploaded signature preview:</p>
                  <div className="border-2 border-green-300 rounded-lg bg-white p-4">
                    <img src={uploadedSignature} alt="Uploaded signature" className="max-h-40 mx-auto" />
                  </div>
                  <div className="flex justify-center mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change Image
                    </Button>
                  </div>
                </div>
              )}

              {/* Retain Mode */}
              {signatureMode === "retain" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <svg className="w-12 h-12 mx-auto text-blue-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800 font-medium">Existing signature will be retained</p>
                  <p className="text-xs text-blue-600 mt-1">The document will keep any previously added signature</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              onClick={handleReturnToExtract}
            >
              Go Back
            </Button>
            <Button
              data-testid="submit-signature-button"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-3"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {signatureMode === "retain" ? "Retain & Continue" : "Apply Signature & Continue"}
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignaturePage;